import { Injectable } from '@angular/core';
import { EmitterService, ListHelperService, PopUpService, ProfileService, StoreService } from 'core/services';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { MarsDebatesService } from './mars-debates.service';

@Injectable()
export class MarsDebatesListService {
  userRole: any;
  dataSource: TableDatasource;

  constructor(
    public translate: TranslateService,
    public profileService: ProfileService,
    public listHelper: ListHelperService,
    public popupService: PopUpService,
    public marsDebatesService: MarsDebatesService
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  async getDatasource(listId, options: any = {}) {
    const dataSource = new TableDatasource({
      sort: '-created_at',
      columns: await this.getColumns(),
      load: (props) => this.fetch(props),
      ...options
    });
    await dataSource.refreshData();
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch({ page, limit, sort, search, filters }) {
    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      ...filters
    };
    return this.marsDebatesService.fetch(paginationOptions).then((response) => response);
  }

  async getColumns() {
    const columns = [
      {
        source: 'questionTitle',
        label: this.translate.instant('customizations.documents.questions.text'),
        dataType: DataTypes.string,
        sortable: true
      },
      {
        source: 'question.choices',
        label: this.translate.instant('nouns.answer'),
        dataType: DataTypes.array,
        style: { width: '80px' },
        sortable: false,
        cellRenderer: (renderer, value) => {
          const rightChoice = ['#acf1ed'];
          const wrongChoice = ['#e1e4e8'];
          return renderer`
            <gl-tooltip posRight light>
              <gl-tag slot="trigger" .colors="${wrongChoice}" .label="${value.length}"></gl-tag>
              ${value.map(
                (answer) =>
                  renderer`<p><gl-tag .colors="${answer.isCorrect ? rightChoice : wrongChoice}" label="${
                    answer.text
                  }"></gl-tag></p>`
              )}
            </gl-tooltip>
          `;
        }
      },
      {
        source: 'session',
        label: this.translate.instant('debates.seasons'),
        dataType: DataTypes.number,
        sortable: true
      },
      {
        source: 'votes',
        label: this.translate.instant('debates.votes'),
        dataType: DataTypes.string,
        sortable: false,
        cellRenderer: (renderer, value) => {
          const positiveVotes = value ? value.positive || 0 : 0;
          const negativeVotes = value ? value.negative || 0 : 0;
          const totalVotes = positiveVotes + negativeVotes;
          const positiveVotePercentage = totalVotes > 0.0 ? Math.ceil((positiveVotes / totalVotes) * 100) : 0;
          const negativeVotePercentage = totalVotes > 0.0 ? Math.round((negativeVotes / totalVotes) * 100) : 0;
          return renderer`
            <gl-icon style="color: var(--success); font-size: 20px" icon="glicon-thumb-up"></gl-icon>
            <span style="margin: var(--gap)">${positiveVotePercentage} %</span>
            <gl-icon style="font-size: 20px" icon="glicon-thumb-down"></gl-icon>
            <span style="margin: var(--gap)">${negativeVotePercentage} %</span>
          `;
        }
      },
      {
        source: 'debate',
        label: this.translate.instant('debates.opinions'),
        dataType: DataTypes.number,
        sortable: true,
        valueParser: (value) => value?.length || 0
      },
      {
        source: 'status',
        label: this.translate.instant('debates.statusLabel'),
        dataType: DataTypes.string,
        sortable: false,
        cellRenderer: (renderer, value = 'default', debate) => {
          const debateIsClosed = value !== this.marsDebatesService.debateStatus.disputed;
          const debateStatusLabel = this.translate.instant(
            debateIsClosed ? 'debates.status.closed' : 'debates.status.open'
          );
          const questionStatusLabel = this.translate.instant(`questions.status.${value.toLowerCase()}`);
          const questionStatusColor =
            value === this.marsDebatesService.debateStatus.certified ? '--success' : '--error';
          return renderer`
            ${debateStatusLabel} &nbsp;
            ${
              debateIsClosed
                ? renderer`(<span style="color: var(${questionStatusColor})">${questionStatusLabel}</span>)`
                : ''
            }
          `;
        }
      },
      {
        alignment: 'right',
        showInColumnChooser: false,
        visible: this.profileService.canExecute('mars.debates.edit'),
        style: { width: '75px' },
        cellRenderer: (renderer, value, debate) => {
          return renderer`
            <gl-button id="${debate.id}" 
              icon icon-center="glicon-edit" 
              @click="${() => this.listHelper.run(() => this.editDebate(debate))}"
            ></gl-button>`;
        }
      }
    ];
    return columns;
  }

  editDebate(debate) {
    EmitterService.get('editMarsDebate').emit(debate);
  }
}
